import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import {
  IntegrationCategory,
  IntegrationScope,
  IntegrationTab,
  IntegrationViewMode,
} from '../../pages/private/Integration/types/integrationCatalog';
import { ZustandStores } from '../utils';
import { createIndexedDBStorage } from '../utils/indexedDBAdapter';

interface IntegrationPreferencesState {
  view: IntegrationViewMode;
  tab: IntegrationTab;
  category: IntegrationCategory;
  scope: IntegrationScope;
  dismissedGoogleRestoreOffers: Record<string, number>;
}

interface IntegrationPreferencesActions {
  setView: (view: IntegrationViewMode) => void;
  setTab: (tab: IntegrationTab) => void;
  setCategory: (category: IntegrationCategory) => void;
  setScope: (scope: IntegrationScope) => void;
  dismissGoogleRestoreOffer: (gmailAddress: string, capturedAt: number) => void;
  reset: () => void;
}

type IntegrationPreferencesStore = IntegrationPreferencesState &
  IntegrationPreferencesActions;

const INITIAL_STATE: IntegrationPreferencesState = {
  view: IntegrationViewMode.GRID,
  tab: IntegrationTab.ALL,
  category: IntegrationCategory.ALL,
  scope: IntegrationScope.PERSONAL,
  dismissedGoogleRestoreOffers: {},
};

export const useIntegrationPreferencesStore =
  create<IntegrationPreferencesStore>()(
    persist(
      (set) => ({
        ...INITIAL_STATE,
        setView: (view) => set({ view }),
        setTab: (tab) => set({ tab }),
        setCategory: (category) => set({ category }),
        setScope: (scope) => set({ scope }),
        dismissGoogleRestoreOffer: (gmailAddress, capturedAt) =>
          set((state) => ({
            dismissedGoogleRestoreOffers: {
              ...state.dismissedGoogleRestoreOffers,
              [gmailAddress]: capturedAt,
            },
          })),
        reset: () => set(INITIAL_STATE),
      }),
      {
        name: ZustandStores.INTEGRATION_PREFERENCES,
        storage: createJSONStorage(() => createIndexedDBStorage()),
        version: 1,
        partialize: (state) => ({
          view: state.view,
          tab: state.tab,
          category: state.category,
          scope: state.scope,
          dismissedGoogleRestoreOffers: state.dismissedGoogleRestoreOffers,
        }),
        migrate: (persistedState) => ({
          ...INITIAL_STATE,
          ...(persistedState as Partial<IntegrationPreferencesState>),
        }),
      },
    ),
  );
